import { AxiosErrorResponse } from '@/@types/Errors'
import {
  CreateOfferRequest,
  Currency,
  OfferData,
} from '@/@types/Products/Offers'
import { useAuth } from '@/auth/Auth'
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect'
import CurrencyPriceInput from '@/components/shared/CurrencyPriceInput/CurrencyPriceInput'
import { Input } from '@/components/shared/Input/Input'
import PaymentMethodSelector from '@/components/shared/PaymentMethodSelector/PaymentMethodSelector'
import ProductUuidBadge from '@/components/Products/product-uuid-badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { toast } from '@/hooks/use-toast'
import {
  formatCurrency,
  getOfferCurrencyOptions,
  getPaymentFrequencyOptions,
  getPaymentTypeOptions,
  getTrialIntervalOptions,
  getTrialModeOptions,
} from '@/lib/select-options'
import { OffersService } from '@/services/offers.service'
import useModalStore from '@/store/useModalStore'
import { formatCheckoutDisplayName } from '@/utils/formatCheckoutDisplayName'
import { getApiErrorMessage } from '@/utils/translateApiError'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Eye, Info, Loader2 } from 'lucide-react'
import React, { ChangeEvent, Dispatch, SetStateAction, useEffect } from 'react'
import { Path, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { offerSchema } from '../validations/offer-schema'

export default function EditOffer({
  offer,
  categoryType,
  modalName = 'funnel-edit-offer',
  productName,
}: {
  offer: OfferData | null
  categoryType: string
  modalName?: string
  productName?: string
}) {
  const { t } = useTranslation(['offers', 'common'])
  const { closeModal } = useModalStore()
  const { id } = useParams()
  const [formattedPrice, setFormattedPrice] = React.useState('')
  const [formattedTrialPrice, setFormattedTrialPrice] = React.useState(() =>
    offer?.trial_price
      ? formatCurrency(
          Number(offer.trial_price) / 100,
          offer?.currency || Currency.Brl,
        )
      : '',
  )
  const { authData } = useAuth()
  const queryClient = useQueryClient()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = useForm<CreateOfferRequest>({
    shouldUnregister: false,
    resolver: zodResolver(offerSchema(categoryType, t)),
    defaultValues: {
      name: offer?.name || '',
      sku: offer?.sku || '',
      price: parseFloat(String(offer?.price)) || 0,
      currency: offer?.currency || Currency.Brl,
      payment_type: offer?.payment_type || 'single',
      recurring_interval:
        offer?.payment_type === 'recurring'
          ? offer.recurring_interval
          : undefined,
      trial_enabled: !!offer?.trial_enabled,
      trial_mode: offer?.trial_mode || '',
      trial_price: offer?.trial_price ?? null,
      trial_interval: offer?.trial_interval || '',
      is_affiliate_enabled: offer?.is_affiliate_enabled || false,
      affiliate_commission_cycles: offer?.affiliate_commission_cycles ?? null,
      payment_methods:
        offer?.paymentMethods
          ?.map((method) => method?.type)
          .filter((type): type is string => type !== undefined) || [],
      is_active: offer?.is_active ?? true,
    },
  })

  const defaultValues = watch()
  const checkoutPreview =
    formatCheckoutDisplayName(productName, watch('name')) ||
    t('common:offerNameCheckoutPreviewPlaceholder')

  const canOfferRecurring = React.useMemo(
    () =>
      authData?.can_offer_recurring ??
      authData?.available_payment_methods?.some(
        (method) => method.supports_recurring,
      ) ??
      false,
    [authData?.can_offer_recurring, authData?.available_payment_methods],
  )

  const { mutate, isPending: isEditingOffer } = useMutation({
    mutationFn: async (data: CreateOfferRequest) => {
      if (!id || !offer?.id) {
        throw new Error('Product id and offer id are required')
      }

      await OffersService.editOffer({
        productId: id,
        data,
        offerId: String(offer.id),
      })
    },
    onMutate: async (updatedData: CreateOfferRequest) => {
      await queryClient.cancelQueries({ queryKey: [`offers-${id}`] })

      const previousQueries = queryClient.getQueriesData({
        queryKey: [`offers-${id}`],
      })

      const optimisticOffer: OfferData = {
        ...offer!,
        name: updatedData.name,
        sku: updatedData.sku || '',
        price: updatedData.price,
        price_formatted: formatCurrency(
          updatedData.price / 100,
          updatedData.currency ?? Currency.Brl,
        ),
        currency: updatedData.currency,
        payment_type: updatedData.payment_type,
        recurring_interval: updatedData.recurring_interval,
        is_affiliate_enabled: updatedData.is_affiliate_enabled,
        affiliate_commission_cycles:
          updatedData.affiliate_commission_cycles ?? null,
        is_active: updatedData.is_active,
        paymentMethods:
          updatedData.payment_methods?.map((method) => ({
            name: method,
            type: method,
          })) || [],
        updated_at: new Date().toISOString(),
      }

      queryClient.setQueriesData(
        { queryKey: [`offers-${id}`], exact: false },
        (oldData: { data?: OfferData[] } | undefined) => {
          if (!oldData || !offer?.id) return oldData

          return {
            ...oldData,
            data: (oldData.data ?? []).map((o) =>
              o.id === offer.id ? optimisticOffer : o,
            ),
          }
        },
      )

      return { previousQueries, optimisticOffer }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`offers-${id}`],
      })
      queryClient.invalidateQueries({
        queryKey: [`checkouts-${id}`],
      })

      toast({
        variant: 'success',
        description: t('offer_saved'),
      })
      closeModal(modalName)
    },
    onError: (error: AxiosError<AxiosErrorResponse>, _variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }

      toast({
        variant: 'error',
        description: getApiErrorMessage(error, t),
      })
    },
  })

  const onSubmit = async (data: CreateOfferRequest) => {
    if (data.payment_type === 'single') {
      const {
        recurring_interval: _recurringInterval,
        trial_enabled: _trialEnabled,
        trial_mode: _trialMode,
        trial_price: _trialPrice,
        trial_interval: _trialInterval,
        ...dataWithoutRecurringInterval
      } = data
      mutate({
        ...dataWithoutRecurringInterval,
        trial_enabled: false,
        trial_mode: null,
        trial_price: null,
        trial_interval: null,
        affiliate_commission_cycles: null,
      } as CreateOfferRequest)
    } else {
      mutate({
        ...data,
        trial_enabled: !!data.trial_enabled,
        trial_mode: data.trial_enabled ? data.trial_mode : null,
        trial_price:
          data.trial_enabled && data.trial_mode === 'promotional'
            ? data.trial_price
            : null,
        trial_interval: data.trial_enabled ? data.trial_interval : null,
        affiliate_commission_cycles: data.is_affiliate_enabled
          ? data.affiliate_commission_cycles ?? null
          : null,
      })
    }
  }

  const handlePriceChange = (
    e: ChangeEvent<HTMLInputElement>,
    setFormattedValue: Dispatch<SetStateAction<string>>,
    fieldName: Path<CreateOfferRequest>,
  ) => {
    const rawValue = e.target.value.replace(/\D/g, '')
    const valueInCents = parseInt(rawValue || '0', 10)
    setFormattedValue(
      formatCurrency(
        valueInCents / 100,
        defaultValues?.currency ?? Currency.Brl,
      ),
    )
    setValue(fieldName, valueInCents)
  }

  useEffect(() => {
    if (defaultValues?.currency) {
      setFormattedPrice(
        formatCurrency(watch('price') / 100, defaultValues.currency),
      )
      const trialPrice = watch('trial_price')
      if (trialPrice) {
        setFormattedTrialPrice(
          formatCurrency(trialPrice / 100, defaultValues.currency),
        )
      } else if (offer?.trial_price) {
        setFormattedTrialPrice(
          formatCurrency(
            Number(offer.trial_price) / 100,
            defaultValues.currency,
          ),
        )
      }
    }
  }, [watch('currency')])

  useEffect(() => {
    if (defaultValues?.payment_type !== 'recurring') {
      return
    }

    const eligibleMethods = (authData?.available_payment_methods || [])
      .filter((method) => method.supports_recurring)
      .map((method) => method.type)

    const currentMethods = defaultValues.payment_methods || []
    const filteredMethods = currentMethods.filter((method) =>
      eligibleMethods.includes(method),
    )

    if (filteredMethods.length !== currentMethods.length) {
      setValue('payment_methods', filteredMethods)
    }
  }, [
    defaultValues?.payment_type,
    authData?.available_payment_methods,
    defaultValues.payment_methods,
    setValue,
  ])

  if (!offer) {
    return null
  }

  return (
    <section
      className="flex flex-col justify-between h-full"
      data-cy="modal-edit-offer"
    >
      <SheetHeader className="md:p-6 p-4">
        <SheetTitle className="flex gap-2">{t('edit_offer')} </SheetTitle>
      </SheetHeader>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between h-full"
      >
        <section className="flex flex-col gap-4 px-6">
          <div className="flex gap-2 items-center">
            <Switch
              checked={defaultValues?.is_active}
              onCheckedChange={(value) => setValue('is_active', value)}
              data-cy="switch-offer-active"
            />
            <Label>{t('common:activate_offer')}</Label>
          </div>
          {authData?.public_api_enabled && (
            <ProductUuidBadge
              uuid={offer.id}
              label={t('common:offerUuidLabel')}
            />
          )}
          <div className="space-y-2">
            <Input
              {...register('name')}
              label={t('name')}
              placeholder={t('name_placeholder')}
              data-cy="input-offer-name"
              helperText={errors.name?.message}
              tooltip={t(
                categoryType === 'pharmaceutical' || categoryType === 'physical'
                  ? 'common:offerNameTooltipEdit'
                  : 'common:offerNameTooltipEditDigital',
              )}
            />
            <div className="flex items-start gap-2 rounded-md border border-colorBorder bg-[#09090B] px-3 py-2.5">
              <Eye className="mt-0.5 h-4 w-4 shrink-0 text-muted" />
              <p className="text-sm leading-relaxed">
                <span className="text-muted">
                  {t('common:offerNameCheckoutPreview')}
                </span>{' '}
                <span className="font-medium text-interactive">
                  {checkoutPreview}
                </span>
              </p>
            </div>
          </div>
          <CustomSelect
            options={getPaymentTypeOptions(t, canOfferRecurring)}
            label={t('payment_type')}
            placeholder={t('payment_holder')}
            data-cy="select-payment-type"
            onValueChange={(value) => {
              setValue('payment_type', value)
              clearErrors('payment_type')
              clearErrors('recurring_interval')
              clearErrors('trial_mode')
              clearErrors('trial_price')
              clearErrors('trial_interval')
              if (value === 'recurring' && !watch('recurring_interval')) {
                setValue('recurring_interval', 'monthly')
              }
              if (value !== 'recurring') {
                setValue('trial_enabled', false)
                setValue('trial_mode', '')
                setValue('trial_price', null)
                setValue('trial_interval', '')
                setFormattedTrialPrice('')
                setValue('affiliate_commission_cycles', null)
              }
            }}
            value={defaultValues.payment_type}
            helperText={errors.payment_type?.message}
          />
          {defaultValues.payment_type === 'recurring' && (
            <>
              <CustomSelect
                options={getPaymentFrequencyOptions(t)}
                label={t('recurrency')}
                placeholder={t('recurrency_placeholder')}
                data-cy="select-recurring-interval"
                onValueChange={(value) => {
                  setValue('recurring_interval', value)
                  clearErrors('recurring_interval')
                  const currentTrial = watch('trial_interval')
                  const allowed = getTrialIntervalOptions(t, value)
                  if (
                    currentTrial &&
                    !allowed.some((option) => option.value === currentTrial)
                  ) {
                    setValue('trial_interval', '')
                    clearErrors('trial_interval')
                  }
                }}
                helperText={errors.recurring_interval?.message}
                value={defaultValues.recurring_interval}
              />

              <div className="flex flex-col gap-3">
                <label
                  htmlFor="trial_enabled"
                  className="text-gray-300 text-sm"
                >
                  {t('common:trialEnabledLabel')}
                </label>
                <Switch
                  id="trial_enabled"
                  checked={!!defaultValues.trial_enabled}
                  onCheckedChange={(checked) => {
                    setValue('trial_enabled', checked)
                    if (!checked) {
                      setValue('trial_mode', '')
                      setValue('trial_price', null)
                      setValue('trial_interval', '')
                      setFormattedTrialPrice('')
                      clearErrors('trial_mode')
                      clearErrors('trial_price')
                      clearErrors('trial_interval')
                    }
                  }}
                  data-cy="switch-trial-enabled"
                />
              </div>

              {!!defaultValues.trial_enabled && (
                <div className="flex flex-col gap-4 rounded-xl border border-colorBorder p-4">
                  <CustomSelect
                    options={getTrialModeOptions(t)}
                    label={t('common:trialModeLabel')}
                    placeholder={t('common:trialModePlaceholder')}
                    data-cy="select-trial-mode"
                    onValueChange={(value) => {
                      setValue('trial_mode', value)
                      clearErrors('trial_mode')
                      if (value !== 'promotional') {
                        setValue('trial_price', null)
                        setFormattedTrialPrice('')
                        clearErrors('trial_price')
                      }
                    }}
                    value={defaultValues.trial_mode || ''}
                    helperText={errors.trial_mode?.message}
                  />
                  {defaultValues.trial_mode === 'free' && (
                    <p className="text-xs text-muted">
                      {t('common:trialModeHintFree')}
                    </p>
                  )}
                  {defaultValues.trial_mode === 'promotional' && (
                    <p className="text-xs text-muted">
                      {t('common:trialModeHintPromotional')}
                    </p>
                  )}

                  {defaultValues.trial_mode === 'promotional' && (
                    <>
                      <CurrencyPriceInput
                        formattedPrice={formattedTrialPrice}
                        onPriceChange={(e) => {
                          handlePriceChange(
                            e,
                            setFormattedTrialPrice,
                            'trial_price',
                          )
                          clearErrors('trial_price')
                        }}
                        currency={defaultValues.currency ?? Currency.Brl}
                        onCurrencyChange={(value) => {
                          setValue('currency', value as Currency)
                          clearErrors('currency')
                        }}
                        currencies={getOfferCurrencyOptions(
                          defaultValues.payment_type ?? 'single',
                        ).map((option) => option.value)}
                        label={t('common:trialPriceLabel')}
                        placeholder={t('value_placeholder')}
                        dataCy="input-trial-price"
                      />
                      {errors.trial_price?.message && (
                        <p className="-mt-2 text-sm text-error">
                          {errors.trial_price?.message}
                        </p>
                      )}
                    </>
                  )}

                  <CustomSelect
                    options={getTrialIntervalOptions(
                      t,
                      defaultValues.recurring_interval,
                    )}
                    label={t('common:trialIntervalLabel')}
                    placeholder={t('common:trialIntervalPlaceholder')}
                    data-cy="select-trial-interval"
                    onValueChange={(value) => {
                      setValue('trial_interval', value)
                      clearErrors('trial_interval')
                    }}
                    value={defaultValues.trial_interval || ''}
                    helperText={errors.trial_interval?.message}
                  />
                  {defaultValues.trial_interval &&
                    !errors.trial_interval?.message && (
                      <p className="text-xs text-muted">
                        {t('common:trialDurationHint', {
                          unit: t(
                            `common:trialDurationUnit.${defaultValues.trial_interval}`,
                          ),
                        })}
                      </p>
                    )}
                </div>
              )}
            </>
          )}
          <CurrencyPriceInput
            formattedPrice={formattedPrice}
            onPriceChange={(e) => {
              handlePriceChange(e, setFormattedPrice, 'price')
              clearErrors('price')
            }}
            currency={defaultValues.currency ?? Currency.Brl}
            onCurrencyChange={(value) => {
              setValue('currency', value as Currency)
              clearErrors('currency')
            }}
            currencies={getOfferCurrencyOptions(
              defaultValues.payment_type ?? 'single',
            ).map((option) => option.value)}
            label={t('value')}
            placeholder={t('value_placeholder')}
            dataCy="input-offer-price"
            helperText={errors.currency?.message}
          />
          {errors.price?.message && (
            <p className="-mt-2 text-sm text-error">{errors.price?.message}</p>
          )}

          {categoryType === 'pharmaceutical' && (
            <Input
              {...register('sku')}
              label={'SKU'}
              placeholder={t('common:skuPlaceholder')}
              data-cy="input-offer-sku"
              helperText={errors.sku?.message}
            />
          )}
          <PaymentMethodSelector
            availableMethods={authData?.available_payment_methods || []}
            selectedMethods={defaultValues.payment_methods}
            onChange={(methods) => setValue('payment_methods', methods)}
            recurringMode={defaultValues.payment_type === 'recurring'}
            className="border-y py-5 my-4 border-y-colorBorder"
            emptyMessage={t('common:payment_methods_contact_support')}
          />

          <div className="flex items-center gap-2">
            <Switch
              id="is_affiliate_enabled"
              checked={defaultValues.is_affiliate_enabled}
              onCheckedChange={(checked) => {
                setValue('is_affiliate_enabled', checked)
                if (!checked) {
                  setValue('affiliate_commission_cycles', null)
                }
              }}
              data-cy="switch-affiliate"
            />
            <label
              htmlFor="is_affiliate_enabled"
              className="text-gray-300 text-sm"
            >
              {t('affiliate_enabled')}
            </label>
          </div>
          {defaultValues.payment_type === 'recurring' &&
            defaultValues.is_affiliate_enabled && (
              <CustomSelect
                options={[
                  { value: 'lifetime', label: t('affiliateCommissionLifetime') },
                  ...Array.from({ length: 12 }, (_, index) => ({
                    value: String(index + 1),
                    label: t('affiliateCommissionCycles', { count: index + 1 }),
                  })),
                ]}
                label={t('affiliateCommissionCyclesLabel')}
                placeholder={t('affiliateCommissionLifetime')}
                onValueChange={(value) => {
                  setValue(
                    'affiliate_commission_cycles',
                    value === 'lifetime' ? null : Number(value),
                  )
                }}
                value={
                  defaultValues.affiliate_commission_cycles
                    ? String(defaultValues.affiliate_commission_cycles)
                    : 'lifetime'
                }
              />
            )}

          <Alert className="h-auto mb-4" variant="info">
            <Info className="h-4 w-4" />
            <AlertDescription>{t('alert_text')}</AlertDescription>
          </Alert>
        </section>
        <div className="flex border-t items-center justify-end gap-4 px-6 py-4 w-full max-sm:pb-12">
          <Button
            variant="ghost"
            onClick={() => closeModal(modalName)}
            type="button"
          >
            {t('cancel')}
          </Button>
          <Button
            disabled={isEditingOffer}
            type="submit"
            data-cy="btn-save-offer"
          >
            {isEditingOffer ? (
              <span className="flex items-center gap-2">
                <Loader2 className="animate-spin" />
                {t('saving')}
              </span>
            ) : (
              t('save_offer')
            )}
          </Button>
        </div>
      </form>
    </section>
  )
}
